import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import '../styles/StrainProfile.css';

// ── Color helpers ─────────────────────────────────────────────────────────────
const CAT_COLORS = { Hybrid: '#4ade80', Indica: '#a78bfa', Sativa: '#fb923c', Unknown: '#64748b' };

function scoreColor(score) {
  if (score == null) return '#475569';
  if (score >= 0.8)  return '#22c55e';
  if (score >= 0.4)  return '#4ade80';
  if (score >= 0.0)  return '#a3e635';
  if (score >= -0.4) return '#fbbf24';
  if (score >= -0.7) return '#f97316';
  return '#ef4444';
}

const CANN_COLORS = { THC: '#f59e0b', CBD: '#34d399', THCV: '#a78bfa', CBC: '#60a5fa', CBG: '#fb923c' };

function stars(rating) {
  const full = Math.round(rating);
  return '★'.repeat(full) + '☆'.repeat(5 - full);
}

// ── Chart: Horizontal Bars ────────────────────────────────────────────────────
function HBars({ items, colorFn }) {
  if (!items || items.length === 0) return <p className="sp-nodata">No data available.</p>;
  const maxV = Math.max(...items.map(d => d.value || 0), 1);
  return (
    <div className="sp-hbar">
      {items.map(d => {
        const pct = Math.max((d.value / maxV) * 100, d.value > 0 ? 2 : 0);
        const color = colorFn ? colorFn(d) : '#4ade80';
        return (
          <div className="sp-hbar-row" key={d.name}>
            <span className="sp-hbar-label" title={d.name}>{d.name}</span>
            <div className="sp-hbar-track">
              <div className="sp-hbar-fill" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="sp-hbar-val">{d.value ?? '—'}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Donut Chart ───────────────────────────────────────────────────────────────
const PALETTE = ['#4ade80','#22d3ee','#a78bfa','#fbbf24','#f472b6','#60a5fa','#fb923c','#34d399','#e879f9','#94a3b8','#f87171','#38bdf8'];

function Donut({ items, size = 140 }) {
  if (!items || items.length === 0) return <p className="sp-nodata">No data.</p>;
  const total = items.reduce((s, d) => s + (d.value || 0), 0);
  if (total === 0) return <p className="sp-nodata">No votes recorded.</p>;

  const cx = size / 2, cy = size / 2, R = size * 0.43, r = size * 0.25;
  let angle = -Math.PI / 2;
  const arcs = items.map((d, i) => {
    const sweep = (d.value / total) * 2 * Math.PI;
    const a1 = angle, a2 = angle + sweep;
    const la = sweep > Math.PI ? 1 : 0;
    const p = (a, radius) => [cx + radius * Math.cos(a), cy + radius * Math.sin(a)];
    const [ox1, oy1] = p(a1, R), [ox2, oy2] = p(a2, R);
    const [ix1, iy1] = p(a1, r), [ix2, iy2] = p(a2, r);
    const path = `M${ix1.toFixed(1)},${iy1.toFixed(1)}L${ox1.toFixed(1)},${oy1.toFixed(1)}A${R},${R},0,${la},1,${ox2.toFixed(1)},${oy2.toFixed(1)}L${ix2.toFixed(1)},${iy2.toFixed(1)}A${r},${r},0,${la},0,${ix1.toFixed(1)},${iy1.toFixed(1)}Z`;
    angle = a2;
    return { ...d, path, pct: d.value / total, color: d.color || PALETTE[i % PALETTE.length] };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size, flexShrink: 0 }}>
        {arcs.map((a, i) => (
          <path key={i} d={a.path} fill={a.color} opacity={0.88}>
            <title>{a.name}: {a.value} votes ({(a.pct * 100).toFixed(1)}%)</title>
          </path>
        ))}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
          fill="#e2e8f0" fontSize={11} fontWeight="700">{total} votes</text>
      </svg>
      <div style={{ flex: 1, minWidth: 120 }}>
        {arcs.map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5, fontSize: '0.75rem' }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: a.color, flexShrink: 0 }} />
            <span style={{ color: '#94a3b8', flex: 1 }}>{a.name}</span>
            <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{(a.pct * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Score Bar (single item) ───────────────────────────────────────────────────
function ScoreBar({ score, maxAbs = 5 }) {
  if (score == null) return null;
  const isPos = score >= 0;
  const pct = Math.min(Math.abs(score) / maxAbs * 100, 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
      <span style={{ fontSize: '0.65rem', color: '#475569', flex: '0 0 50px', textAlign: 'right' }}>score</span>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          marginLeft: isPos ? '50%' : `${50 - pct / 2}%`,
          width: `${pct / 2}%`,
          height: '100%',
          background: scoreColor(score),
          borderRadius: 4,
        }} />
      </div>
      <span style={{ fontSize: '0.7rem', color: scoreColor(score), fontWeight: 600, flex: '0 0 52px' }}>
        {score > 0 ? '+' : ''}{score.toFixed(3)}
      </span>
    </div>
  );
}

// ── Radar Chart ───────────────────────────────────────────────────────────────
function Radar({ items, color = '#4ade80', size = 320 }) {
  if (!items || items.length < 3) return null;
  const CX = size / 2, CY = size / 2 + 10, R = size * 0.35;
  const n = items.length;
  const maxV = Math.max(...items.map(d => d.value), 0.001);
  const angle = i => (i / n) * 2 * Math.PI - Math.PI / 2;
  const outer = i => [CX + R * Math.cos(angle(i)), CY + R * Math.sin(angle(i))];
  const dataP = (v, i) => { const r = (v / maxV) * R; return [CX + r * Math.cos(angle(i)), CY + r * Math.sin(angle(i))]; };
  const label = i => [CX + (R + 24) * Math.cos(angle(i)), CY + (R + 24) * Math.sin(angle(i))];
  const poly = pts => pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ') + 'Z';
  const dataPts = items.map((d, i) => dataP(d.value, i));

  return (
    <svg viewBox={`0 0 ${size} ${size + 20}`} style={{ width: '100%', maxWidth: size }}>
      {[0.25, 0.5, 0.75, 1].map(s => {
        const pts = items.map((_, i) => { const r = R * s; const a = angle(i); return [CX + r * Math.cos(a), CY + r * Math.sin(a)]; });
        return <path key={s} d={poly(pts)} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={1} />;
      })}
      {items.map((_, i) => {
        const [ox, oy] = outer(i);
        return <line key={i} x1={CX} y1={CY} x2={ox.toFixed(1)} y2={oy.toFixed(1)} stroke="rgba(255,255,255,0.06)" strokeWidth={1} />;
      })}
      <path d={poly(dataPts)} fill={color + '22'} stroke={color} strokeWidth={2} strokeLinejoin="round" />
      {dataPts.map(([x, y], i) => (
        <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r={4} fill={color} stroke="#0a1a0e" strokeWidth={1.5}>
          <title>{items[i].name}: {items[i].value}</title>
        </circle>
      ))}
      {items.map((d, i) => {
        const [lx, ly] = label(i);
        return (
          <text key={i} x={lx.toFixed(1)} y={ly.toFixed(1)} textAnchor="middle" dominantBaseline="middle"
            fill="#94a3b8" fontSize={10} fontWeight="500">{d.name}</text>
        );
      })}
    </svg>
  );
}

// ── Tab: Flavors ──────────────────────────────────────────────────────────────
function FlavorsTab({ data }) {
  const flavors = Object.values(data.flavors || {});
  if (flavors.length === 0) return <p className="sp-nodata">No flavor data for this strain.</p>;

  const sorted = [...flavors].sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const donutItems = sorted.map((f, i) => ({ name: f.name, value: f.votes || 0, color: PALETTE[i % PALETTE.length] }));
  const radarItems = sorted.map(f => ({ name: f.name, value: f.votes || 0 }));

  return (
    <div>
      <div className="sp-chart-grid">
        <div className="sp-card">
          <div className="sp-card-title">Flavor Votes — Ranked</div>
          <HBars
            items={sorted.map(f => ({ name: f.name, value: f.votes || 0, score: f.score }))}
            colorFn={d => scoreColor(d.score)}
          />
        </div>
        <div className="sp-card">
          <div className="sp-card-title">Flavor Vote Share</div>
          <Donut items={donutItems} size={138} />
        </div>
      </div>
      <div className="sp-chart-grid">
        <div className="sp-card">
          <div className="sp-card-title">Sentiment Scores per Flavor</div>
          <HBars
            items={[...flavors].sort((a, b) => (b.score || 0) - (a.score || 0))
              .map(f => ({ name: f.name, value: Math.round((f.score || 0) * 1000 + 5000), score: f.score }))}
            colorFn={d => scoreColor(d.score)}
          />
          <div style={{ marginTop: 12 }}>
            {[...flavors].sort((a, b) => (b.score || 0) - (a.score || 0)).map(f => (
              <div key={f.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.73rem', padding: '2px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                <span style={{ color: '#94a3b8' }}>{f.name}</span>
                <span style={{ color: scoreColor(f.score), fontWeight: 600 }}>{f.score != null ? (f.score > 0 ? '+' : '') + f.score.toFixed(3) : '—'}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="sp-card">
          <div className="sp-card-title">Flavor Distribution Radar</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Radar items={radarItems} color="#4ade80" size={280} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Effects ──────────────────────────────────────────────────────────────
function EffectsTab({ data }) {
  const effects = Object.values(data.effects || {});
  if (effects.length === 0) return <p className="sp-nodata">No effect data for this strain.</p>;

  const sorted = [...effects].sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const donutItems = sorted.map((e, i) => ({ name: e.name, value: e.votes || 0, color: PALETTE[i % PALETTE.length] }));
  const radarItems = sorted.map(e => ({ name: e.name, value: e.votes || 0 }));

  return (
    <div>
      <div className="sp-chart-grid">
        <div className="sp-card">
          <div className="sp-card-title">Effect Votes — Ranked</div>
          <HBars
            items={sorted.map(e => ({ name: e.name, value: e.votes || 0, score: e.score }))}
            colorFn={d => scoreColor(d.score)}
          />
        </div>
        <div className="sp-card">
          <div className="sp-card-title">Effect Vote Share</div>
          <Donut items={donutItems} size={138} />
        </div>
      </div>
      <div className="sp-chart-grid">
        <div className="sp-card">
          <div className="sp-card-title">Effect Sentiment Scores</div>
          {[...effects].sort((a, b) => (b.score || 0) - (a.score || 0)).map(e => (
            <div key={e.name} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{e.name}</span>
                <span style={{ color: '#475569', fontSize: '0.7rem' }}>{e.votes || 0} votes</span>
              </div>
              <ScoreBar score={e.score} maxAbs={3} />
            </div>
          ))}
        </div>
        <div className="sp-card">
          <div className="sp-card-title">Effect Distribution Radar</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Radar items={radarItems} color="#22d3ee" size={280} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Cannabinoids ─────────────────────────────────────────────────────────
function CannabinoidsTab({ data }) {
  const cannabs = Object.values(data.cannabinoids || {});
  if (cannabs.length === 0) return <p className="sp-nodata">No cannabinoid data for this strain.</p>;

  const withData = cannabs.filter(c => (c.percentile50 || 0) > 0);
  const maxVal = Math.max(...withData.map(c => c.percentile50 || 0), 1);

  return (
    <div>
      <div className="sp-cann-grid" style={{ marginBottom: 20 }}>
        {cannabs.sort((a, b) => (b.percentile50 || 0) - (a.percentile50 || 0)).map(c => {
          const name = c.displayName || c.name;
          const val = c.percentile50 || 0;
          const color = CANN_COLORS[name] || '#4ade80';
          return (
            <div className="sp-cann-tile" key={name} style={{ borderColor: color + '33' }}>
              <span className="sp-cann-val" style={{ color }}>
                {val > 0 ? `${val}%` : 'ND'}
              </span>
              <span className="sp-cann-name">{name}</span>
            </div>
          );
        })}
      </div>

      <div className="sp-chart-grid">
        <div className="sp-card">
          <div className="sp-card-title">Cannabinoid Profile (Percentile-50 %)</div>
          {cannabs.sort((a, b) => (b.percentile50 || 0) - (a.percentile50 || 0)).map(c => {
            const name = c.displayName || c.name;
            const val = c.percentile50 || 0;
            const color = CANN_COLORS[name] || '#4ade80';
            const pct = (val / maxVal) * 100;
            return (
              <div key={name} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: 4 }}>
                  <span style={{ color, fontWeight: 700 }}>{name}</span>
                  <span style={{ color: '#64748b' }}>{val > 0 ? `${val}%` : 'Not detected'}</span>
                </div>
                <div style={{ height: 20, background: 'rgba(255,255,255,0.04)', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${color}99, ${color})`, borderRadius: 5 }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="sp-card">
          <div className="sp-card-title">Relative Cannabinoid Ratios</div>
          {withData.length > 0 ? (
            <Donut
              items={withData.map(c => ({
                name: c.displayName || c.name,
                value: c.percentile50 || 0,
                color: CANN_COLORS[c.displayName || c.name] || '#4ade80',
              }))}
              size={140}
            />
          ) : (
            <p className="sp-nodata">No measurable cannabinoids.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tab: Terpenes ─────────────────────────────────────────────────────────────
function TerpenesTab({ data }) {
  const terps = Object.values(data.terps || {});
  if (terps.length === 0) return <p className="sp-nodata">No terpene data for this strain.</p>;

  const sorted = [...terps].sort((a, b) => (b.score || 0) - (a.score || 0));
  const maxScore = Math.max(...sorted.map(t => t.score || 0), 0.001);
  const radarItems = sorted.map(t => ({ name: t.name, value: t.score || 0 }));

  return (
    <div>
      <div className="sp-chart-grid">
        <div className="sp-card">
          <div className="sp-card-title">Terpene Scores — Ranked</div>
          <div className="sp-terp-list">
            {sorted.map(t => (
              <div className="sp-terp-pill" key={t.name}>
                <span className="sp-terp-name">{t.name}</span>
                <div className="sp-terp-bar">
                  <div className="sp-terp-fill" style={{ width: `${((t.score || 0) / maxScore) * 100}%` }} />
                </div>
                <span className="sp-terp-val">{(t.score || 0).toFixed(4)}</span>
                {t.description && (
                  <span style={{ fontSize: '0.62rem', color: '#475569', marginTop: 4, textAlign: 'center', maxWidth: 110 }}>
                    {t.description.substring(0, 60)}…
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="sp-card">
          <div className="sp-card-title">Terpene Profile Radar</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Radar items={radarItems} color="#a78bfa" size={280} />
          </div>
        </div>
      </div>

      <div className="sp-chart-grid">
        <div className="sp-card sp-card-wide">
          <div className="sp-card-title">Terpene Score Bars</div>
          <HBars
            items={sorted.map(t => ({ name: t.name, value: Math.round((t.score || 0) * 10000) }))}
            colorFn={() => '#a78bfa'}
          />
        </div>
      </div>
    </div>
  );
}

// ── Tab: Medical Relief ───────────────────────────────────────────────────────
function MedicalTab({ data }) {
  const conditions = Object.values(data.conditions || {});
  const symptoms = Object.values(data.symptoms || {});

  if (conditions.length === 0 && symptoms.length === 0)
    return <p className="sp-nodata">No medical data reported for this strain.</p>;

  const condSorted = [...conditions].sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const sympSorted = [...symptoms].sort((a, b) => (b.votes || 0) - (a.votes || 0));

  return (
    <div>
      <div className="sp-chart-grid">
        {condSorted.length > 0 && (
          <div className="sp-card">
            <div className="sp-card-title">Medical Conditions — Vote Count</div>
            <HBars
              items={condSorted.map(c => ({ name: c.name, value: c.votes || 0, score: c.score }))}
              colorFn={d => scoreColor(d.score)}
            />
          </div>
        )}
        {sympSorted.length > 0 && (
          <div className="sp-card">
            <div className="sp-card-title">Symptoms Helped — Vote Count</div>
            <HBars
              items={sympSorted.map(s => ({ name: s.name, value: s.votes || 0, score: s.score }))}
              colorFn={d => scoreColor(d.score)}
            />
          </div>
        )}
      </div>

      <div className="sp-chart-grid">
        {condSorted.length > 0 && (
          <div className="sp-card">
            <div className="sp-card-title">Conditions — Sentiment Scores</div>
            {condSorted.map(c => (
              <div key={c.name} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.77rem' }}>
                  <span style={{ color: '#e2e8f0' }}>{c.name}</span>
                  <span style={{ color: '#475569', fontSize: '0.7rem' }}>{c.votes || 0} votes</span>
                </div>
                <ScoreBar score={c.score} maxAbs={2} />
              </div>
            ))}
          </div>
        )}
        {sympSorted.length > 0 && (
          <div className="sp-card">
            <div className="sp-card-title">Symptoms — Sentiment Scores</div>
            {sympSorted.map(s => (
              <div key={s.name} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.77rem' }}>
                  <span style={{ color: '#e2e8f0' }}>{s.name}</span>
                  <span style={{ color: '#475569', fontSize: '0.7rem' }}>{s.votes || 0} votes</span>
                </div>
                <ScoreBar score={s.score} maxAbs={2} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Side Effects ─────────────────────────────────────────────────────────
function SideEffectsTab({ data }) {
  const negs = Object.values(data.negatives || {});
  if (negs.length === 0) return <p className="sp-nodata">No negative effects reported for this strain.</p>;

  const sorted = [...negs].sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const negColors = ['#ef4444','#f97316','#fbbf24','#fb923c','#f87171','#fca5a5'];
  const donutItems = sorted.map((n, i) => ({ name: n.name, value: n.votes || 0, color: negColors[i] }));

  return (
    <div>
      <div className="sp-chart-grid">
        <div className="sp-card">
          <div className="sp-card-title">Side Effect Votes</div>
          <HBars
            items={sorted.map((n, i) => ({ name: n.name, value: n.votes || 0 }))}
            colorFn={(d, i) => negColors[sorted.findIndex(n => n.name === d.name)] || '#ef4444'}
          />
        </div>
        <div className="sp-card">
          <div className="sp-card-title">Side Effect Distribution</div>
          <Donut items={donutItems} size={138} />
        </div>
      </div>
    </div>
  );
}

// ═══ MAIN PAGE ════════════════════════════════════════════════════════════════

const TABS = [
  { key: 'flavors',     label: 'Flavors',       component: FlavorsTab },
  { key: 'effects',     label: 'Effects',        component: EffectsTab },
  { key: 'cannabinoids',label: 'Cannabinoids',   component: CannabinoidsTab },
  { key: 'terpenes',    label: 'Terpenes',       component: TerpenesTab },
  { key: 'medical',     label: 'Medical Relief', component: MedicalTab },
  { key: 'sideeffects', label: 'Side Effects',   component: SideEffectsTab },
];

const CATEGORIES = ['All', 'Hybrid', 'Indica', 'Sativa'];

export default function StrainProfile() {
  const [index, setIndex]           = useState(null);
  const [indexError, setIndexError] = useState(null);
  const [query, setQuery]           = useState('');
  const [catFilter, setCatFilter]   = useState('All');
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [strainData, setStrainData] = useState(null);
  const [loadingStrain, setLoadingStrain] = useState(false);
  const [activeTab, setActiveTab]   = useState('flavors');

  // Load index once
  useEffect(() => {
    fetch('/straindata/_index.json')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setIndex)
      .catch(e => setIndexError(e.message));
  }, []);

  // Filter index
  const filtered = useMemo(() => {
    if (!index) return [];
    const q = query.toLowerCase().trim();
    return index.filter(s => {
      if (catFilter !== 'All' && s.category !== catFilter) return false;
      if (q && !s.name.toLowerCase().includes(q) && !s.slug.includes(q)) return false;
      return true;
    });
  }, [index, query, catFilter]);

  // Load a specific strain
  const selectStrain = useCallback((slug) => {
    setSelectedSlug(slug);
    setStrainData(null);
    setLoadingStrain(true);
    setActiveTab('flavors');
    fetch(`/straindata/${slug}.json`)
      .then(r => r.json())
      .then(d => { setStrainData(d); setLoadingStrain(false); })
      .catch(() => { setStrainData(null); setLoadingStrain(false); });
  }, []);

  const ActiveTab = strainData ? (TABS.find(t => t.key === activeTab)?.component || FlavorsTab) : null;
  const catColor = strainData ? (CAT_COLORS[strainData.category] || '#64748b') : '#4ade80';

  return (
    <Layout>
      <div className="sp-layout">

        {/* ── Left: Search + List ── */}
        <div className="sp-sidebar">
          <div className="sp-search-wrap">
            <div className="sp-search-title">Strain Browser</div>
            <input
              className="sp-search-input"
              type="text"
              placeholder="Search strains…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoComplete="off"
            />
            <div className="sp-filter-row">
              {CATEGORIES.map(c => (
                <button
                  key={c}
                  className={`sp-filter-btn${catFilter === c ? ' active' : ''}`}
                  onClick={() => setCatFilter(c)}
                >{c}</button>
              ))}
            </div>
            <div className="sp-result-count">
              {index ? `${filtered.length.toLocaleString()} / ${index.length.toLocaleString()} strains` : 'Loading index…'}
            </div>
          </div>

          <div className="sp-list">
            {indexError && <div style={{ padding: 14, color: '#ef4444', fontSize: '0.78rem' }}>Failed to load index: {indexError}</div>}
            {!index && !indexError && (
              <div style={{ padding: 20, color: '#475569', fontSize: '0.8rem' }}>Loading strain list…</div>
            )}
            {filtered.slice(0, 500).map(s => (
              <div
                key={s.slug}
                className={`sp-list-item${selectedSlug === s.slug ? ' selected' : ''}`}
                onClick={() => selectStrain(s.slug)}
              >
                <span className="sp-list-name">{s.name}</span>
                <div className="sp-list-meta">
                  <span className={`sp-cat-badge sp-cat-${s.category || 'Unknown'}`}>{s.category || 'Unknown'}</span>
                  {s.rating > 0 && <span style={{ color: '#fbbf24' }}>★ {s.rating.toFixed(1)}</span>}
                  {s.topEffect && <span>{s.topEffect}</span>}
                </div>
              </div>
            ))}
            {filtered.length > 500 && (
              <div style={{ padding: '10px 14px', color: '#475569', fontSize: '0.72rem' }}>
                …and {(filtered.length - 500).toLocaleString()} more. Refine your search.
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Profile ── */}
        <div className="sp-main">
          {!selectedSlug && (
            <div className="sp-empty">
              <span className="sp-empty-icon">🌿</span>
              <span className="sp-empty-text">
                Select a strain from the list<br />to view its data visualizations
              </span>
            </div>
          )}

          {selectedSlug && loadingStrain && (
            <div className="sp-loading">
              <div className="sp-spinner" />
              Loading strain data…
            </div>
          )}

          {selectedSlug && !loadingStrain && strainData && (
            <>
              {/* Header */}
              <div className="sp-profile-header">
                <div className="sp-profile-top">
                  <div>
                    <h2 className="sp-profile-name">{strainData.name}</h2>
                    <div className="sp-profile-badges">
                      <span className={`sp-cat-badge sp-cat-${strainData.category}`} style={{ fontSize: '0.75rem', padding: '3px 10px' }}>
                        {strainData.category}
                      </span>
                      {strainData.rating > 0 && (
                        <>
                          <span className="sp-rating">{stars(strainData.rating)}</span>
                          <span className="sp-reviews">{strainData.rating.toFixed(1)} · {strainData.reviewCount?.toLocaleString()} reviews</span>
                        </>
                      )}
                      {strainData.topEffect && (
                        <span className="sp-top-effect">Top effect: {strainData.topEffect}</span>
                      )}
                    </div>
                    {strainData.description && (
                      <p className="sp-description">{strainData.description}</p>
                    )}
                  </div>
                </div>

                <div className="sp-tabs">
                  {TABS.map(t => (
                    <button
                      key={t.key}
                      className={`sp-tab${activeTab === t.key ? ' active' : ''}`}
                      onClick={() => setActiveTab(t.key)}
                    >{t.label}</button>
                  ))}
                </div>
              </div>

              {/* Tab content */}
              <div className="sp-body">
                <ActiveTab data={strainData} />
              </div>
            </>
          )}

          {selectedSlug && !loadingStrain && !strainData && (
            <div className="sp-empty">
              <span className="sp-empty-text">Failed to load strain data.</span>
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}
